import { DocumentRemovalActions } from "./DocumentRemovalActions";
import { archiveTitle, ArchiveGroups, ArchiveMetadata, ArchiveTitleButton } from "./ArchivePresentation";
import { PropertyList, PropertyItem } from "@/components/ui/property-list";
import { DocumentWorkspace } from "@/components/ui/document-workspace";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { useSearchParams } from "react-router-dom";
import { DocumentDataPanel } from "@/components/dokumenti/DocumentDataPanel";
import { StructuredRecordLink } from "@/components/dokumenti/StructuredEvidence";
import { DocumentTypeCombobox } from "@/components/dokumenti/DocumentTypeCombobox";
import { DocumentDescription } from "@/components/dokumenti/DocumentDescription";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import {
  dohvatiDokumente,
  dohvatiAiPostavke,
  dohvatiAiPrijedlog,
  dohvatiSadrzajDokumenta,
  dohvatiTipoveDokumenata,
  ucitajDokument,
  odluciOAIPrijedlogu,
  verificirajDokument,
  vratiDokument,
  type AiDocumentVerificationFeature,
  type DocumentAiSuggestion,
  type DokumentDjelatnika,
  type TipDokumenta,
} from "@/lib/dokumenti";
import { datum } from "@/lib/format";

const HEALTH_CODES = new Set(["ra2_medical_fitness", "pur_medical_certificate", "permit_medical_certificate"]);

export function DokumentiTab({ radnikId }: { radnikId: string }) {
  const { can } = useAuth();
  const [searchParams] = useSearchParams();
  const [dokumenti, setDokumenti] = useState<DokumentDjelatnika[] | null>(null);
  const [tipovi, setTipovi] = useState<TipDokumenta[]>([]);
  const [odabraniId, setOdabraniId] = useState<string | null>(searchParams.get("document"));
  const [greska, setGreska] = useState<string | null>(null);
  const [aiPostavke, setAiPostavke] =
    useState<AiDocumentVerificationFeature | null>(null);

  const canCreate = can("dokument", "create");
  const canReadHealth = can("zdravlje", "read");
  const canUpdateHealth = can("zdravlje", "update");
  // Whoever may delete a document is the one who may look at what was deleted.
  const canSeeDeleted = can("dokument", "confirm_delete");
  const [prikaziObrisane, setPrikaziObrisane] = useState(false);
  const [undoId, setUndoId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  useEffect(() => {
    if (!undoId) return;
    const timer = window.setTimeout(() => setUndoId(null), 30_000);
    return () => window.clearTimeout(timer);
  }, [undoId]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [listExpanded, setListExpanded] = useState(false);
  const vidljiviDokumenti = useMemo(
    () =>
      (dokumenti ?? []).filter(
        (dokument) =>
          (!HEALTH_CODES.has(dokument.document_type_code ?? "") || canReadHealth) &&
          // The server already leaves these out unless they were asked for.
          // Repeated here so turning the switch back off is immediate rather
          // than waiting on a round trip.
          (prikaziObrisane || dokument.status !== "deleted"),
      ),
    [dokumenti, canReadHealth, prikaziObrisane],
  );

  useEffect(() => {
    const kontroler = new AbortController();
    Promise.all([
      dohvatiDokumente(radnikId, kontroler.signal, prikaziObrisane),
      canCreate
        ? dohvatiTipoveDokumenata(kontroler.signal)
        : Promise.resolve([]),
    ])
      .then(([noviDokumenti, noviTipovi]) => {
        setDokumenti(noviDokumenti);
        setTipovi(noviTipovi.filter(type => type.document_group !== "payroll" && type.code !== "sick_leave_certificate"));
        const otvorivi = [...noviDokumenti.filter((d) => d.status === "active"), ...noviDokumenti.filter((d) => d.status === "superseded")];
        setOdabraniId((trenutni) => trenutni ?? otvorivi[0]?.id ?? null);
      })
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska(errorMessage(error, "Dokumenti se ne mogu učitati."));
      });
    return () => kontroler.abort();
  }, [canCreate, radnikId, prikaziObrisane]);

  useEffect(() => {
    const kontroler = new AbortController();
    dohvatiAiPostavke(kontroler.signal)
      .then(setAiPostavke)
      .catch(() => {
        if (!kontroler.signal.aborted) {
          setAiPostavke({ enabled: false, confidence_threshold: 0.85 });
        }
      });
    return () => kontroler.abort();
  }, []);

  const odabrani =
    vidljiviDokumenti.find(
      (d) => d.id === odabraniId && d.status !== "deleted",
    ) ?? null;

  async function restore(id: string) {
    setRestoring(true);
    try {
      const document = await vratiDokument(radnikId, id);
      setDokumenti(rows => rows?.some(row => row.id === id) ? rows.map(row => row.id === id ? document : row) : [document, ...(rows ?? [])]);
      setOdabraniId(id); setUndoId(null); setGreska(null);
    } catch (error: unknown) {
      setGreska(errorMessage(error, "Dokument se ne može vratiti."));
    } finally { setRestoring(false); }
  }

  if (greska && dokumenti === null) {
    return <EmptyState naslov="Dokumenti nisu dostupni" objasnjenje={greska} />;
  }
  if (dokumenti === null) return <SkeletonTablica redaka={3} />;

  return (
    <div aria-label="Radna površina dokumenata" className="flex min-h-0 flex-1 flex-col gap-3 lg:overflow-hidden">
      {undoId && <div role="status" className="flex flex-wrap items-center gap-3"><span>Dokument je obrisan.</span><Button className="min-h-11" disabled={restoring} onClick={() => void restore(undoId)}>Poništi</Button></div>}
      {greska ? <Alert variant="error">{greska}</Alert> : null}
      <div className={`avm-document-archive ${listExpanded || !odabrani ? "is-expanded" : "is-compact"}`}>
        <section aria-label="Popis i unos dokumenata" className="flex min-h-0 min-w-0 flex-col gap-3 lg:overflow-y-auto">
          <div className="flex shrink-0 flex-wrap gap-2">
            {odabrani && <Button variant="secondary" aria-expanded={listExpanded} onClick={() => setListExpanded(value => !value)}>{listExpanded ? "Suzi popis" : "Proširi popis"}</Button>}
            {canCreate && <Button type="button" variant="outline" aria-expanded={uploadOpen} aria-controls="archive-upload" onClick={() => setUploadOpen((value) => !value)}>{uploadOpen ? "Zatvori unos" : "Učitaj dokument"}</Button>}
            {canSeeDeleted && <Button type="button" variant="outline" aria-pressed={prikaziObrisane} onClick={() => setPrikaziObrisane((value) => !value)}>{prikaziObrisane ? "Sakrij obrisane" : "Prikaži obrisane"}</Button>}
          </div>
          {vidljiviDokumenti.length === 0 ? <EmptyState naslov="Nema učitanih dokumenata" objasnjenje="Dokumenti će se prikazati nakon što ih ovlaštena osoba učita." /> :
            <div aria-label="Popis arhivskih dokumenata" className={`min-h-32 shrink-0 overflow-auto ${uploadOpen ? "max-h-[35vh]" : "lg:min-h-0 lg:flex-1"}`}>
              {([ ["active", "Aktivni dokumenti"], ["superseded", "Povijesni dokumenti"], ["deleted", "Obrisani dokumenti"] ] as const).map(([status, title]) => {
                const rows = vidljiviDokumenti.filter((document) => document.status === status);
                return rows.length ? <section key={status} aria-label={title} className="mb-3 space-y-2"><h3 className="font-semibold">{title}</h3><ArchiveGroups documents={rows}>{group => <PopisDokumenata onRestore={canSeeDeleted ? (id) => void restore(id) : undefined} restoring={restoring} dokumenti={group} odabraniId={odabraniId} compact={!listExpanded && !!odabrani} onSelect={(id) => { setOdabraniId(id); setListExpanded(false); }} />}</ArchiveGroups></section> : null;
              })}
            </div>}
          {canCreate && uploadOpen && <div id="archive-upload" className="shrink-0">
            <UploadForma radnikId={radnikId} tipovi={tipovi} canUpdateHealth={canUpdateHealth}
              onUploaded={(dokument) => {
                setDokumenti((current) => [dokument, ...(current ?? [])]);
                setOdabraniId(dokument.id); setGreska(null); setUploadOpen(false);
              }} onError={setGreska} />
          </div>}
        </section>
        <section aria-label="Podaci i pregled dokumenta" className="flex min-h-0 min-w-0 flex-col gap-3 lg:overflow-hidden">
          {odabrani ? <>
            {canSeeDeleted && (!HEALTH_CODES.has(odabrani.document_type_code ?? "") || canUpdateHealth) && <DocumentRemovalActions key={`removal-${odabrani.id}`} radnikId={radnikId} documentId={odabrani.id} onRemoved={(document, moved) => {
              setDokumenti(rows => moved ? (rows ?? []).filter(row => row.id !== document.id) : (rows ?? []).map(row => row.id === document.id ? document : row));
              setOdabraniId(null); setGreska(null);
              if (!moved) setUndoId(document.id);
            }} onError={setGreska} />}
            <PregledDokumenta key={odabrani.id} radnikId={radnikId} dokument={odabrani}
            canVerify={odabrani.status === "active" && can("dokument", "update") && (!HEALTH_CODES.has(odabrani.document_type_code ?? "") || canUpdateHealth)}
            aiPostavke={aiPostavke} onVerified={(dokument) => {
              setDokumenti((current) => (current ?? []).map((item) => item.id === dokument.id ? dokument : item));
            }} onError={setGreska} /></> : <p className="p-3 text-sm text-muted-foreground">Odaberite dokument za pregled.</p>}
        </section>
      </div>
    </div>
  );
}

function UploadForma({
  radnikId,
  tipovi,
  canUpdateHealth,
  onUploaded,
  onError,
}: {
  radnikId: string;
  tipovi: TipDokumenta[];
  canUpdateHealth: boolean;
  onUploaded: (dokument: DokumentDjelatnika) => void;
  onError: (poruka: string) => void;
}) {
  const dostupniTipovi = tipovi.filter(
    (tip) =>
      tip.active_version !== null &&
      tip.active_version.requirement_level !== "reference_only" &&
      (!HEALTH_CODES.has(tip.code) || canUpdateHealth),
  );
  const [tip, setTip] = useState("");
  const [naziv, setNaziv] = useState("");
  const [opis, setOpis] = useState("");
  const [datoteka, setDatoteka] = useState<File | null>(null);
  const [izdano, setIzdano] = useState("");
  const [istice, setIstice] = useState("");
  const [salje, setSalje] = useState(false);
  const odabraniTip = dostupniTipovi.find((stavka) => stavka.code === tip);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!tip || !datoteka || !naziv.trim()) {
      onError("Unesite naziv te odaberite tip dokumenta i datoteku.");
      return;
    }
    if (odabraniTip?.active_version?.expiry_required && !istice) {
      onError("Datum isteka obvezan je za odabrani tip dokumenta.");
      return;
    }
    setSalje(true);
    try {
      const dokument = await ucitajDokument(radnikId, {
        documentTypeCode: tip,
        naziv: naziv.trim(),
        opis: opis.trim() || undefined,
        datoteka,
        issuedOn: izdano,
        expiresOn: istice,
      });
      onUploaded(dokument);
      setDatoteka(null);
      setNaziv(""); setOpis("");
      setIzdano("");
      setIstice("");
      form.reset();
      setTip("");
    } catch (error: unknown) {
      onError(errorMessage(error, "Dokument se ne može učitati."));
    } finally {
      setSalje(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="grid gap-4 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 sm:grid-cols-2"
    >
      <div className="flex flex-col gap-2 sm:col-span-2">
        <h2 className="font-semibold">Učitaj dokument</h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          PDF, JPEG ili PNG, najviše 15 MB.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2"><Label htmlFor="document-name">Naziv (obvezno)</Label><Input id="document-name" required value={naziv} onChange={(event) => setNaziv(event.target.value)} /></div>
      <div className="flex flex-col gap-2 sm:col-span-2"><Label htmlFor="document-description">Opis</Label><DocumentDescription id="document-description" value={opis} onChange={setOpis} /></div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="document-type">Tip dokumenta</Label>
        <DocumentTypeCombobox id="document-type" value={tip} onChange={setTip} types={dostupniTipovi} required />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="document-file">Datoteka</Label>
        <Input
          id="document-file"
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(event) => setDatoteka(event.target.files?.[0] ?? null)}
          required
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="document-issued">Datum izdavanja</Label>
        <Input
          id="document-issued"
          type="date"
          value={izdano}
          onChange={(event) => setIzdano(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="document-expires">Datum isteka</Label>
        <Input
          id="document-expires"
          type="date"
          value={istice}
          onChange={(event) => setIstice(event.target.value)}
          required={odabraniTip?.active_version?.expiry_required ?? false}
        />
      </div>
      <div className="sm:col-span-2">
        <Button type="submit" disabled={salje}>
          {salje ? "Učitavanje…" : "Učitaj dokument"}
        </Button>
      </div>
    </form>
  );
}

function PopisDokumenata({
  dokumenti,
  odabraniId,
  onSelect,
  compact = false,
  onRestore,
  restoring,
}: {
  dokumenti: DokumentDjelatnika[];
  odabraniId: string | null;
  onSelect: (id: string) => void;
  compact?: boolean;
  onRestore?: (id: string) => void;
  restoring?: boolean;
}) {
  return (
    <div className="avm-surface overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
      <table className={`w-full border-collapse text-left ${compact ? "avm-document-list--compact" : ""}`}>
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="oznaka px-4 py-3">Naziv / tip</th>
          </tr>
        </thead>
        <tbody>
          {dokumenti.map((dokument) => (
            <tr
              key={dokument.id}
              className={redakKlasa(dokument, odabraniId)}
            >
              <td className="px-4 py-3">
                {/* A deleted document opens nothing: the panel on the right
                    verifies and uploads, and neither applies any more. */}
                {dokument.status === "deleted" ? (
                  <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{imeDokumenta(dokument)}</span>{onRestore && <Button className="min-h-11" disabled={restoring} onClick={() => onRestore(dokument.id)}>Vrati</Button>}</div>
                ) : (
                  <ArchiveTitleButton
                    aria-current={dokument.id === odabraniId ? "true" : undefined}
                    onClick={() => onSelect(dokument.id)}
                  >
                    {imeDokumenta(dokument)}
                  </ArchiveTitleButton>
                )}
                <ArchiveMetadata document={dokument.document_type_code === "occupational_safety_training" ? { ...dokument, expires_on: null } : dokument} verified={dokument.verificiran} historical={dokument.status === "superseded"} deleted={dokument.status === "deleted"} />
                {dokument.status === "deleted" && dokument.deleted_at && <div className="text-xs text-muted-foreground">Obrisan {datum(dokument.deleted_at)}</div>}
                <StructuredRecordLink document={dokument} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function imeDokumenta(dokument: DokumentDjelatnika): string {
  return (
    archiveTitle(dokument)
  );
}

/** Deleted rows read as struck-through history, not as part of the record. */
function redakKlasa(
  dokument: DokumentDjelatnika,
  odabraniId: string | null,
): string {
  const rub = "border-b border-[var(--color-border)]";
  if (dokument.status === "deleted") {
    return `${rub} text-[var(--color-muted-foreground)] opacity-60`;
  }
  if (dokument.status === "superseded") return `${rub} bg-slate-100 text-slate-500`;
  return dokument.id === odabraniId ? `${rub} bg-muted` : `${rub} last:border-b-0`;
}

function PregledDokumenta({
  radnikId,
  dokument,
  canVerify,
  aiPostavke,
  onVerified,
  onError,
}: {
  radnikId: string;
  dokument: DokumentDjelatnika;
  canVerify: boolean;
  aiPostavke: AiDocumentVerificationFeature | null;
  onVerified: (dokument: DokumentDjelatnika) => void;
  onError: (poruka: string) => void;
}) {
  const [sadrzajUrl, setSadrzajUrl] = useState<string | null>(null);
  const [dohvatGotov, setDohvatGotov] = useState(false);
  const [aiPrijedlog, setAiPrijedlog] = useState<DocumentAiSuggestion | null>(
    null,
  );
  const [predispuniIstice, setPredispuniIstice] = useState<string | null>(null);

  useEffect(() => {
    if (!dokument.has_content) {
      return;
    }
    const kontroler = new AbortController();
    let objectUrl: string | null = null;
    dohvatiSadrzajDokumenta(radnikId, dokument.id, kontroler.signal)
      .then((blob) => {
        if (kontroler.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setSadrzajUrl(objectUrl);
      })
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        onError(errorMessage(error, "Sadržaj dokumenta nije dostupan."));
      })
      .finally(() => {
        if (!kontroler.signal.aborted) setDohvatGotov(true);
      });
    return () => {
      kontroler.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [dokument.has_content, dokument.id, onError, radnikId]);

  useEffect(() => {
    if (!aiPostavke?.enabled) return;
    const kontroler = new AbortController();
    dohvatiAiPrijedlog(radnikId, dokument.id, kontroler.signal)
      .then(setAiPrijedlog)
      .catch((error: unknown) => {
        if (!kontroler.signal.aborted) {
          onError(errorMessage(error, "AI prijedlog nije dostupan."));
        }
      });
    return () => kontroler.abort();
  }, [aiPostavke?.enabled, dokument.id, onError, radnikId]);

  const review = <div className="space-y-4">
      {aiPostavke?.enabled && aiPrijedlog ? (
        <AiPrijedlogKartica
          radnikId={radnikId}
          dokument={dokument}
          prijedlog={aiPrijedlog}
          threshold={aiPostavke.confidence_threshold}
          canDecide={canVerify}
          onDecision={(novi, prefill) => {
            setAiPrijedlog(novi);
            if (prefill) setPredispuniIstice(prefill);
          }}
          onError={onError}
        />
      ) : null}

      {canVerify ? (
        <VerifikacijaForma
          key={predispuniIstice ?? "manual"}
          radnikId={radnikId}
          dokument={dokument}
          prefillExpiresOn={predispuniIstice}
          onVerified={onVerified}
          onError={onError}
        />
      ) : null}
  </div>;
  return <DocumentWorkspace
    layout="comparison"
    title={imeDokumenta(dokument)}
    url={sadrzajUrl}
    mime={dokument.mime_type}
    filename={dokument.original_filename}
    summary={<>
      <span>{dokument.document_type_name}</span>
      <span>{dokument.issued_on ? datum(dokument.issued_on) : "—"} – {dokument.trajno ? "Trajno" : dokument.expires_on ? datum(dokument.expires_on) : "—"}</span>
      <Badge ton={dokument.verificiran ? "ok" : "warn"}>{dokument.verificiran ? "Provjeren" : "Nije provjeren"}</Badge>
      {HEALTH_CODES.has(dokument.document_type_code ?? "") && <span>Pristup zdravstvenom dokumentu evidentira se.</span>}
    </>}
    fallback={dokument.has_content && !dohvatGotov ? <SkeletonTablica redaka={3} /> : <EmptyState naslov="Sadržaj nije dostupan" objasnjenje="Za ovaj zapis nije povezana datoteka." />}
    tabs={[
      { id: "data", label: "Podaci", content: <><DocumentDataPanel key={dokument.id} radnikId={radnikId} documentId={dokument.id} document={dokument} onDocumentUpdated={onVerified} />{!canVerify && review}</> },
      ...(canVerify ? [{ id: "review", label: "Provjera", content: review }] : []),
    ]}
  />;
}

const FIELD_LABELS: Record<string, string> = {
  nositelj_ime: "Ime nositelja",
  nositelj_prezime: "Prezime nositelja",
  broj_isprave: "Broj isprave",
  datum_izdavanja: "Datum izdavanja",
  datum_isteka: "Datum isteka",
  izdavatelj: "Izdavatelj",
  kategorije_vozacke: "Kategorije vozačke",
  vrsta_stroja: "Vrsta stroja",
  ocjena: "Ocjena sposobnosti",
  datum_sljedeceg_pregleda: "Sljedeći pregled",
};

const MATCH_LABELS: Record<DocumentAiSuggestion["podudarnost_profila"], string> = {
  podudara_se: "Podudara se s profilom",
  djelomicno: "Djelomično se podudara",
  ne_podudara_se: "Ne podudara se s profilom",
};

const SUGGESTION_STATUS_LABELS: Record<DocumentAiSuggestion["status"], string> = {
  predlozeno: "Predloženo",
  prihvaceno: "Prihvaćeno u formu",
  odbaceno: "Odbačeno",
  isteklo: "Isteklo",
};

function AiPrijedlogKartica({
  radnikId,
  dokument,
  prijedlog,
  threshold,
  canDecide,
  onDecision,
  onError,
}: {
  radnikId: string;
  dokument: DokumentDjelatnika;
  prijedlog: DocumentAiSuggestion;
  threshold: number;
  canDecide: boolean;
  onDecision: (prijedlog: DocumentAiSuggestion, prefill: string | null) => void;
  onError: (poruka: string) => void;
}) {
  const [salje, setSalje] = useState(false);

  async function odluci(decision: "prihvaceno" | "odbaceno") {
    setSalje(true);
    try {
      const rezultat = await odluciOAIPrijedlogu(
        radnikId,
        dokument.id,
        decision,
      );
      onDecision(rezultat.suggestion, rezultat.prefill_expires_on);
    } catch (error: unknown) {
      onError(errorMessage(error, "Odluka o AI prijedlogu nije spremljena."));
    } finally {
      setSalje(false);
    }
  }

  return (
    <section className="avm-surface flex flex-col gap-4 rounded-md border border-border bg-muted/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">AI prijedlog</h3>
        <Badge ton="idle">{SUGGESTION_STATUS_LABELS[prijedlog.status]}</Badge>
      </div>
      <PropertyList>
        {Object.entries(prijedlog.ekstrahirana_polja).map(([key, value]) => <PropertyItem key={key} label={FIELD_LABELS[key] ?? key}>{Array.isArray(value) ? value.join(", ") : String(value)}</PropertyItem>)}
        <PropertyItem label="Pouzdanost">{Math.round(prijedlog.confidence * 100)} %</PropertyItem>
        <PropertyItem label="Podudarnost">{MATCH_LABELS[prijedlog.podudarnost_profila]}</PropertyItem>
      </PropertyList>
      {prijedlog.confidence < threshold ? (
        <Alert className="border-amber-500 bg-amber-50 text-amber-950">
          Niska pouzdanost — obvezna usporedba s izvornikom
        </Alert>
      ) : null}
      {canDecide && prijedlog.status === "predlozeno" ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={salje} onClick={() => odluci("prihvaceno")}>
            Prihvati u formu
          </Button>
          <Button type="button" variant="outline" disabled={salje} onClick={() => odluci("odbaceno")}>
            Odbaci
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function VerifikacijaForma({
  radnikId,
  dokument,
  prefillExpiresOn,
  onVerified,
  onError,
}: {
  radnikId: string;
  dokument: DokumentDjelatnika;
  prefillExpiresOn: string | null;
  onVerified: (dokument: DokumentDjelatnika) => void;
  onError: (poruka: string) => void;
}) {
  const [istice, setIstice] = useState(
    prefillExpiresOn ?? dokument.expires_on ?? "",
  );
  const [napomena, setNapomena] = useState(dokument.napomena ?? "");
  const [potvrda, setPotvrda] = useState(false);
  const [sprema, setSprema] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!potvrda) return;
    setSprema(true);
    try {
      const verificirani = await verificirajDokument(radnikId, dokument.id, {
        expires_on: dokument.trajno ? null : istice || null,
        napomena: napomena || null,
      });
      onVerified(verificirani);
      setPotvrda(false);
    } catch (error: unknown) {
      onError(errorMessage(error, "Provjera se ne može spremiti."));
    } finally {
      setSprema(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 border-t border-[var(--color-border)] pt-4"
    >
      <h3 className="font-semibold">Ručna provjera</h3>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`verify-expiry-${dokument.id}`}>
          Ispravljeni datum isteka
        </Label>
        <Input
          id={`verify-expiry-${dokument.id}`}
          type="date"
          value={istice}
          onChange={(event) => setIstice(event.target.value)}
          required={!dokument.trajno && dokument.expiry_required}
          disabled={dokument.trajno}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`verify-note-${dokument.id}`}>Napomena</Label>
        <textarea
          id={`verify-note-${dokument.id}`}
          value={napomena}
          maxLength={2000}
          onChange={(event) => setNapomena(event.target.value)}
          className="min-h-24 rounded-md border border-border bg-background px-3 py-2"
        />
      </div>
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={potvrda}
          onChange={(event) => setPotvrda(event.target.checked)}
          className="mt-1 size-4"
        />
        <span>Potvrđujem da metapodaci odgovaraju prikazanom izvorniku.</span>
      </label>
      <div>
        <Button type="submit" disabled={!potvrda || sprema}>
          {sprema ? "Spremanje…" : "Provjeri dokument"}
        </Button>
      </div>
    </form>
  );
}
